// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors, type ReduxNext, type ReduxStore } from '@makemydeal/dr-shared-store';

// consts/enums
import { PAYMENT_FAILURE } from '../actionTypes/deskingActionTypes';
import { UPDATED_SELL_RATE_OVERRIDE } from '../actionTypes/offerActionTypes';

// actions
import { clearManualFieldOverrides } from '../actions/manualFieldOverridesActionCreators';
import { updateSellRateForSelectedTermUPQ, updateSellRateOverride } from '../actions/offerRedux';

// selectors
import * as manualFieldOverridesSelectors from '../selectors/manualFieldOverridesSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';

// utils
import { createStoreScopeState } from '../utils/storeScopeState';
import { isPqOverrideRecoveryOutcome } from './__common__/pqOverrideRecovery';

export const SELL_RATE_FIELD_EDIT_META = 'sellRateFieldEdit';
const PREVIOUS_SELL_RATE_META = 'previousSellRateOverride';
const SELL_RATE_FIELD_KEY = 'sellRate';

type PreviousSellRate = {
    editId: number;
    offerType: OfferType;
    termValue: number | undefined;
    dealValue: number | undefined;
    wasUserProgramQuotes: boolean;
    wasMarkedOverridden: boolean;
};

const getLatestEdits = createStoreScopeState<Map<string, number>>(() => new Map());
let editSeq = 0;

const editKeyOf = (offerType: OfferType, months: number) => `${offerType}|${months}`;

const termOverrideOf = (state: StateTree, offerType: OfferType, months: number) =>
    offerReduxSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, months, false);

const dealOverrideOf = (state: StateTree, offerType: OfferType) =>
    offerReduxSelectors.getIsUserProgramQuotes(state, offerType)
        ? offerReduxSelectors.getSellRateOverride(state, false, offerType)
        : undefined;

const recordPreviousSellRate = (store: ReduxStore<StateTree>, action: AnyFSA): void => {
    const state = store.getState();
    const offerType: OfferType = action.meta.selectedOfferType || offerReduxSelectors.getCurrentOfferType(state);
    const editId = ++editSeq;
    getLatestEdits(store).set(editKeyOf(offerType, action.payload.months), editId);

    const previous: PreviousSellRate = {
        editId,
        offerType,
        termValue: termOverrideOf(state, offerType, action.payload.months),
        dealValue: dealOverrideOf(state, offerType),
        wasUserProgramQuotes: offerReduxSelectors.getIsUserProgramQuotes(state, offerType),
        wasMarkedOverridden: manualFieldOverridesSelectors.getManualFieldOverrides(state).includes(SELL_RATE_FIELD_KEY)
    };
    action.meta = { ...action.meta, [PREVIOUS_SELL_RATE_META]: previous };
};

const isHandledByPqOverrideRecovery = (state: StateTree, failure: AnyFSA, previous: PreviousSellRate): boolean =>
    isPqOverrideRecoveryOutcome(failure) &&
    !previous.wasUserProgramQuotes &&
    offerReduxSelectors.getIsUserProgramQuotes(state, previous.offerType);

const rollBackSellRate = (store: ReduxStore<StateTree>, failure: AnyFSA): void => {
    const state = store.getState();
    const originalAction = failure.meta.originalAction;
    const previous: PreviousSellRate = originalAction.meta[PREVIOUS_SELL_RATE_META];
    const { months, sellRate } = originalAction.payload;
    if (isHandledByPqOverrideRecovery(state, failure, previous)) return;
    if (getLatestEdits(store).get(editKeyOf(previous.offerType, months)) !== previous.editId) return;
    if (termOverrideOf(state, previous.offerType, months) !== sellRate) return;

    const dealValueToKeep = previous.wasUserProgramQuotes ? previous.dealValue : dealOverrideOf(state, previous.offerType);

    const rollback = updateSellRateOverride(previous.termValue, months, { skipPaymentCall: true }, previous.offerType);
    store.dispatch({ ...rollback, meta: { ...rollback.meta, middleware: { payment: false } } });

    if (offerReduxSelectors.getIsUserProgramQuotes(store.getState(), previous.offerType)) {
        store.dispatch(updateSellRateForSelectedTermUPQ(dealValueToKeep));
    }

    if (!previous.wasMarkedOverridden) {
        store.dispatch(clearManualFieldOverrides([SELL_RATE_FIELD_KEY]));
    }
};

export const middleware = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    if (
        action.type === UPDATED_SELL_RATE_OVERRIDE &&
        action.meta?.[SELL_RATE_FIELD_EDIT_META] &&
        featureToggleSelectors.isRollToUpqOnFirstCalculationEnabled(store.getState())
    ) {
        recordPreviousSellRate(store, action);
    }

    const result = next(action);

    const originalAction = action.meta?.originalAction;
    if (
        action.type === PAYMENT_FAILURE &&
        originalAction?.type === UPDATED_SELL_RATE_OVERRIDE &&
        originalAction.meta?.[PREVIOUS_SELL_RATE_META]
    ) {
        rollBackSellRate(store, action);
    }

    return result;
};
